<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="업데이트"
        subtitle="새로운 기능 및 개선사항"
        icon="update"
        iconColor="indigo"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 로딩 -->
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <!-- 업데이트 목록 -->
        <div v-else-if="updates.length > 0" class="space-y-3">
          <div
            v-for="update in updates"
            :key="update.id"
            class="bg-white border border-slate-200 rounded-2xl overflow-hidden"
          >
            <!-- 헤더 (클릭하여 펼치기) -->
            <button
              @click="toggleUpdate(update.id)"
              class="w-full px-4 py-4 flex items-start gap-3 text-left hover:bg-slate-50 transition-colors"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span v-if="update.version" class="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[11px] font-bold rounded flex-shrink-0">{{ update.version }}</span>
                  <span v-if="update.type === 'feature'" class="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[11px] font-bold rounded flex-shrink-0">새 기능</span>
                  <span v-else-if="update.type === 'improvement'" class="px-2 py-0.5 bg-blue-100 text-blue-700 text-[11px] font-bold rounded flex-shrink-0">개선</span>
                  <span v-else-if="update.type === 'fix'" class="px-2 py-0.5 bg-orange-100 text-orange-700 text-[11px] font-bold rounded flex-shrink-0">수정</span>
                  <span v-if="isNew(update.createdAt)" class="px-2 py-0.5 bg-rose-100 text-rose-700 text-[11px] font-bold rounded flex-shrink-0">NEW</span>
                </div>
                <h3 class="text-[15px] font-bold text-slate-900 mb-1">{{ update.title }}</h3>
                <p class="text-[12px] text-slate-400">{{ formatDate(update.createdAt) }}</p>
              </div>
              <svg
                class="w-5 h-5 text-slate-300 transition-transform flex-shrink-0 mt-1"
                :class="{ 'rotate-180': openUpdates.includes(update.id) }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            <!-- 내용 -->
            <div
              v-show="openUpdates.includes(update.id)"
              class="px-4 pb-4 border-t border-slate-100"
            >
              <div class="pt-4">
                <div class="text-[14px] text-slate-700 leading-relaxed prose prose-sm max-w-none" v-html="update.content"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 업데이트 없음 -->
        <div v-else class="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
          </div>
          <p class="text-[15px] font-medium text-slate-500">등록된 업데이트가 없습니다</p>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, onMounted } from 'vue'
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'PartnerUpdatesView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const loading = ref(true)
    const updates = ref([])
    const openUpdates = ref([])

    const fetchUpdates = async () => {
      loading.value = true
      try {
        const q = query(
          collection(db, 'partnerUpdates'),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc')
        )
        const snapshot = await getDocs(q)
        updates.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))

        // 최신 업데이트는 기본으로 펼쳐두기
        if (updates.value.length > 0) {
          openUpdates.value = [updates.value[0].id]
        }
      } catch (error) {
        console.error('업데이트 조회 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const toggleUpdate = (id) => {
      const index = openUpdates.value.indexOf(id)
      if (index > -1) {
        openUpdates.value.splice(index, 1)
      } else {
        openUpdates.value.push(id)
      }
    }

    const isNew = (dateStr) => {
      if (!dateStr) return false
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      return diff < 7 * 24 * 60 * 60 * 1000 // 7일 이내
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    onMounted(() => {
      fetchUpdates()
    })

    return {
      loading,
      updates,
      openUpdates,
      toggleUpdate,
      isNew,
      formatDate
    }
  }
}
</script>
